import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Upload, Linkedin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold text-sm";
const labelCls = "label-mono text-ink/60 block mb-2";

function Field({ label, children, testid }) {
  return <div data-testid={testid}><label className={labelCls}>{label}</label>{children}</div>;
}

export default function AdminProfile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/profile`); setP(data); }
    catch (e) { toast.error("Impossible de charger le profil."); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setRow = (k, i, f, v) => setP((prev) => {
    const rows = prev[k].map((r, j) => (j === i ? { ...r, [f]: v } : r));
    return { ...prev, [k]: rows };
  });
  const addRow = (k, blank) => setP((prev) => ({ ...prev, [k]: [...prev[k], blank] }));
  const delRow = (k, i) => setP((prev) => ({ ...prev, [k]: prev[k].filter((_, j) => j !== i) }));

  const LINKEDIN_RE = /^https:\/\/(www\.|[a-z]{2}\.)?linkedin\.com\//i;
  const linkedinValid = !p?.linkedin_url || LINKEDIN_RE.test(p.linkedin_url.trim());

  const save = async () => {
    if (!linkedinValid) {
      toast.error("URL LinkedIn invalide.", { description: "Format attendu : https://www.linkedin.com/in/…" });
      return;
    }
    setSaving(true);
    try {
      const { slug, name, has_photo, updated_at, ...payload } = p;
      await axios.put(`${API}/admin/profile`, payload);
      toast.success("Profil enregistré.", { description: "La page /majdi-zarougui est à jour." });
      load();
    } catch (e) {
      toast.error("Enregistrement impossible.", { description: e.response?.data?.detail || "Erreur serveur." });
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/profile/photo`, fd);
      toast.success("Photo mise à jour.");
      load();
    } catch (e) {
      toast.error("Upload impossible.", { description: e.response?.data?.detail || "Erreur serveur." });
    } finally { setUploading(false); }
  };

  if (!p) return <div className="p-10 text-ink/50 label-mono" data-testid="profile-admin-loading">Chargement du profil…</div>;

  return (
    <div className="space-y-10" data-testid="profile-admin">
      {/* Identité */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-5">
        <h3 className="font-display text-2xl font-semibold">Identité</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Titre professionnel" testid="f-job-title">
            <input className={inputCls} value={p.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="input-job-title" />
          </Field>
          <Field label="Années d'expérience (ex : 10+)" testid="f-years">
            <input className={inputCls} value={p.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="input-years" />
          </Field>
        </div>
        <Field label="Accroche (chips séparées par •)" testid="f-tagline">
          <input className={inputCls} value={p.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="input-tagline" />
        </Field>
        <Field label="Biographie (un paragraphe par ligne vide)" testid="f-bio">
          <textarea rows={7} className={inputCls} value={(p.bio || []).join("\n\n")}
            onChange={(e) => set("bio", e.target.value.split(/\n\s*\n/).filter(Boolean))} data-testid="input-bio" />
        </Field>
        <Field label="URL LinkedIn (https://…linkedin.com/…)" testid="f-linkedin">
          <div className="flex items-center gap-3">
            <Linkedin size={18} className="text-ink/40 shrink-0" />
            <input className={inputCls} value={p.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)}
              placeholder="https://www.linkedin.com/in/…" data-testid="input-linkedin" />
          </div>
          {!linkedinValid && (
            <p className="text-red-600 text-sm mt-2" data-testid="linkedin-error">
              URL LinkedIn invalide — format attendu : https://www.linkedin.com/in/…
            </p>
          )}
        </Field>
        <Field label="Photo de profil (jpg, png, webp — 8 Mo max)" testid="f-photo">
          <div className="flex items-center gap-4">
            <img src={p.has_photo ? `${API}/profile/photo` : "/images/majdi-zarougui.jpg"} alt="Aperçu photo"
              className="w-16 h-16 rounded-full object-cover object-top border hairline" data-testid="photo-preview" />
            <label className="inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 label-mono cursor-pointer hover:border-ink transition-colors">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Changer la photo
              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="input-photo"
                onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
          </div>
        </Field>
      </section>

      {/* Certifications */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold">Certifications</h3>
          <button onClick={() => addRow("certifications", { name: "", detail: "" })} data-testid="add-cert"
            className="inline-flex items-center gap-2 label-mono px-4 py-2 rounded-full border hairline hover:border-ink transition-colors"><Plus size={14} /> Ajouter</button>
        </div>
        {p.certifications.map((c, i) => (
          <div key={i} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-center" data-testid={`cert-row-${i}`}>
            <input className={inputCls} placeholder="Nom (ex : Lean Six Sigma Black Belt)" value={c.name} onChange={(e) => setRow("certifications", i, "name", e.target.value)} data-testid={`cert-name-${i}`} />
            <input className={inputCls} placeholder="Détail (optionnel)" value={c.detail} onChange={(e) => setRow("certifications", i, "detail", e.target.value)} data-testid={`cert-detail-${i}`} />
            <button onClick={() => delRow("certifications", i)} aria-label="Supprimer" data-testid={`cert-del-${i}`}
              className="w-9 h-9 rounded-full bg-ink/5 text-ink/50 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </section>

      {/* Expériences */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold">Expériences terrain</h3>
          <button onClick={() => addRow("experiences", { sector: "", role: "", period: "", context: "", missions: [], methods: [], results: "" })} data-testid="add-exp"
            className="inline-flex items-center gap-2 label-mono px-4 py-2 rounded-full border hairline hover:border-ink transition-colors"><Plus size={14} /> Ajouter</button>
        </div>
        {p.experiences.map((e, i) => (
          <div key={i} className="rounded-sm border hairline p-5 space-y-3" data-testid={`exp-row-${i}`}>
            <div className="grid md:grid-cols-3 gap-3">
              <input className={inputCls} placeholder="Secteur (ex : Aéronautique)" value={e.sector} onChange={(ev) => setRow("experiences", i, "sector", ev.target.value)} data-testid={`exp-sector-${i}`} />
              <input className={inputCls} placeholder="Poste / rôle" value={e.role} onChange={(ev) => setRow("experiences", i, "role", ev.target.value)} data-testid={`exp-role-${i}`} />
              <input className={inputCls} placeholder="Période (optionnel)" value={e.period} onChange={(ev) => setRow("experiences", i, "period", ev.target.value)} data-testid={`exp-period-${i}`} />
            </div>
            <textarea rows={2} className={inputCls} placeholder="Contexte / missions principales" value={e.context} onChange={(ev) => setRow("experiences", i, "context", ev.target.value)} data-testid={`exp-context-${i}`} />
            <div className="grid md:grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Méthodes (séparées par des virgules)" value={(e.methods || []).join(", ")}
                onChange={(ev) => setRow("experiences", i, "methods", ev.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`exp-methods-${i}`} />
              <div className="flex gap-3">
                <input className={inputCls} placeholder="Résultats (optionnel)" value={e.results} onChange={(ev) => setRow("experiences", i, "results", ev.target.value)} data-testid={`exp-results-${i}`} />
                <button onClick={() => delRow("experiences", i)} aria-label="Supprimer" data-testid={`exp-del-${i}`}
                  className="w-9 h-9 shrink-0 rounded-full bg-ink/5 text-ink/50 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Approche */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-4">
        <h3 className="font-display text-2xl font-semibold">Mon approche (cycle)</h3>
        {p.approach.map((s, i) => (
          <div key={i} className="grid md:grid-cols-[80px_220px_1fr] gap-3 items-center" data-testid={`approach-row-${i}`}>
            <span className="font-display text-2xl font-semibold text-gold">{s.step}</span>
            <input className={inputCls} value={s.title} onChange={(e) => setRow("approach", i, "title", e.target.value)} data-testid={`approach-title-${i}`} />
            <input className={inputCls} value={s.description} onChange={(e) => setRow("approach", i, "description", e.target.value)} data-testid={`approach-desc-${i}`} />
          </div>
        ))}
      </section>

      {/* Expertises */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-4">
        <h3 className="font-display text-2xl font-semibold">Domaines d'expertise</h3>
        {p.expertise_groups.map((g, i) => (
          <div key={i} className="grid md:grid-cols-[260px_1fr] gap-3 items-start" data-testid={`expertise-row-${i}`}>
            <input className={inputCls} value={g.title} onChange={(e) => setRow("expertise_groups", i, "title", e.target.value)} data-testid={`expertise-title-${i}`} />
            <textarea rows={2} className={inputCls} value={(g.items || []).join(", ")}
              onChange={(e) => setRow("expertise_groups", i, "items", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`expertise-items-${i}`} />
          </div>
        ))}
        <p className="label-mono text-ink/40">Compétences séparées par des virgules.</p>
      </section>

      {/* SEO */}
      <section className="rounded-sm border hairline bg-white p-6 md:p-8 space-y-5">
        <h3 className="font-display text-2xl font-semibold">SEO de la page</h3>
        <Field label="Meta Title" testid="f-seo-title">
          <input className={inputCls} value={p.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="input-seo-title" />
        </Field>
        <Field label="Meta Description" testid="f-seo-desc">
          <textarea rows={3} className={inputCls} value={p.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="input-seo-desc" />
        </Field>
      </section>

      <button onClick={save} disabled={saving} data-testid="profile-save-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-60">
        {saving ? <><Loader2 size={16} className="animate-spin" /> Enregistrement…</> : <><Save size={15} /> Enregistrer le profil</>}
      </button>
    </div>
  );
}
